import { MenuItem, Select } from "@material-ui/core";
import React, { Component } from "react";
import styled, { css } from "styled-components";
import { LibraryView } from "common/constants";
import { ActionPermissionsObject, PermissionActionType, WorkspacePlanTier } from "common/interfaces";
import WorkspaceController from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import { getStaticUrl } from "js/config";
import { app } from "js/namespaces";
import UpgradeIcon from "js/Components/UpgradeIcon";
import { t } from "js/i18n";
import { Icon } from "js/Components/Icon";

const OptionsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const OptionItem = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    border: none;
    background: transparent;
    font-size: 16px;
    font-weight: 600;
    font-family: "Source Sans Pro";
    text-transform: uppercase;
    cursor: pointer;
    color: #444;
`;

const CornerUpgradeIcon = styled(UpgradeIcon)`
    position: absolute;
    top: -10px;
    right: -10px;
`;

const IconBadgeWrap = styled.div`
    position: relative;
    display: inline-flex;
    border-radius: 2px;
    padding: 2px;


    ${props => props.selected && css`
        background: rgba(255, 255, 255, 0.2);
    `}
`;

const StyledOptionItem = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 4px;
    padding: 4px 0;

    color: #FFF;
    text-align: center;
    font-family: "Source Sans Pro";
    font-size: 10px;
    font-style: normal;
    font-weight: 600;
    line-height: 120%; /* 12px */
    letter-spacing: 0.5px;

    cursor: pointer;

    &:hover ${IconBadgeWrap} {
        background: rgba(255, 255, 255, 0.10);
        border-radius: 2px;
    }

`;

const StyledIcon = styled(Icon)`
    padding: 8px;
`;

const LogoBox = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    padding: 20px 0;
    cursor: pointer;
`;

export class PresentationLibraryViewSelect extends Component {
    constructor(props) {
        super(props);

        // Set mount time to track navigation to library
        this.libraryMountTime = performance.now();
    }

    handleClickLogo = () => {
        // Ignore logo clicks within 500ms of library mounting to prevent accidental
        // navigation when user clicks home icon then quickly clicks logo
        const timeSinceMount = performance.now() - this.libraryMountTime;
        if (timeSinceMount < 500) {
            return;
        }
        AppController.gotoMarketingPage();
    }

    render() {
        const { libraryView, onSwitchType, isMyPresentationsOnly } = this.props;

        const isBasic = WorkspaceController.plan.tier === WorkspacePlanTier.BASIC;
        const canCreateResources = !app.integrator && WorkspaceController.actionPermissions[ActionPermissionsObject.RESOURCES].create;
        const canUpgradeToCreateResources = !app.integrator && (isBasic || WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.RESOURCES, PermissionActionType.CREATE));
        const canManageSharedSlides = !app.integrator && WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].manage;
        const canUpgradeToManageSharedSlides = !app.integrator && WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.MANAGE);
        const canUpgradeToUseSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, "use");

        const showExtraOptions = !isMyPresentationsOnly && canCreateResources && !app.isMobileOrTablet;

        return (
            <OptionsList>
                {!app.isMobileOrTablet && <LogoBox className="logo-box" onClick={this.handleClickLogo}>
                    <img style={{ width: 24, height: 24 }} className="logo-desktop"
                        src={getStaticUrl("/images/beautifulai-logos/beautifulai-mark-reverse.svg")} />
                </LogoBox>}

                <OptionItem
                    onClick={() => onSwitchType(LibraryView.LIBRARY)}
                    type="button"
                >
                    <StyledOptionItem>
                        <IconBadgeWrap selected={libraryView === LibraryView.LIBRARY}>
                            <StyledIcon size={24} fill={libraryView === LibraryView.LIBRARY} color="#FFF">stacks</StyledIcon>
                        </IconBadgeWrap>
                        {t("Presentations")}
                    </StyledOptionItem>
                </OptionItem>
                {showExtraOptions && (canCreateResources || canUpgradeToCreateResources) && (
                    <OptionItem
                        className={libraryView === LibraryView.MY_RESOURCES ? "selected" : ""}
                        onClick={() => onSwitchType(LibraryView.MY_RESOURCES)}
                        type="button"
                    >
                        <StyledOptionItem>
                            <IconBadgeWrap selected={libraryView === LibraryView.MY_RESOURCES}>
                                <StyledIcon size={24} fill={libraryView === LibraryView.MY_RESOURCES} color="#FFF">palette</StyledIcon>
                                {canUpgradeToCreateResources && <CornerUpgradeIcon />}
                            </IconBadgeWrap>
                            {t("My Themes")}
                        </StyledOptionItem>
                    </OptionItem>
                )}
                {showExtraOptions && (canManageSharedSlides || canUpgradeToManageSharedSlides || canUpgradeToUseSharedResources) && (
                    <OptionItem
                        className={libraryView === LibraryView.TEAM_RESOURCES ? "selected" : ""}
                        onClick={() => onSwitchType(LibraryView.TEAM_RESOURCES)}
                        type="button"
                    >
                        <StyledOptionItem>
                            <IconBadgeWrap selected={libraryView === LibraryView.TEAM_RESOURCES}>
                                <StyledIcon size={24} fill={libraryView === LibraryView.TEAM_RESOURCES} color="#FFF">domain</StyledIcon>
                                {(canUpgradeToManageSharedSlides || canUpgradeToUseSharedResources) && <CornerUpgradeIcon />}
                            </IconBadgeWrap>
                            {t("Team Resources")}
                        </StyledOptionItem>
                    </OptionItem>
                )}
            </OptionsList>
        );
    }
}

export default PresentationLibraryViewSelect;
